// ============================================================================
// Facebook Page — listAccounts capability method.
//
// Enumerates the Pages the authenticated user can publish to. Marketing
// shows this in the post-target picker; the user selects one and we persist
// `providerAccountId` against `marketing_post_targets`.
//
// Why we hit `/me/accounts` instead of `/{user_id}/accounts`:
//   - `/me/...` is auth-token resolved server-side; we never have to look up
//     the user id from the token, so a stolen token still can't be used to
//     enumerate someone else's Pages.
//
// Pagination: FB's default page size is 25; we ask for 100 and walk
// `paging.next` up to a safety cap (MAX_FETCH_PAGES) so a misbehaving
// provider can't spin us forever.
// ============================================================================

import type {
  SocialAccount,
  SocialListAccountsArgs,
  SocialListAccountsResult,
  SocialResult,
  SocialPostingCallCtx,
} from '../../capabilities/social-posting'
import { errorFromResponse } from './errors'
import { FB_PAGE_GRAPH_API_BASE } from './oauth'
import type { FacebookPageAccountsListResponse, FacebookPageAccountRow } from './types'

// ============================================================================
// Constants
// ============================================================================

/** Safety cap on pagination walks. 10 pages × 100 entries = 1000 Pages, more
 *  than enough for any real account. */
const MAX_FETCH_PAGES = 10

/** Per-page request size — Meta's max is 100. */
const DEFAULT_LIMIT = 100

// ============================================================================
// listAccounts
// ============================================================================

export interface ListAccountsDeps {
  /** Injectable fetch — defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
  /** Override the Graph API base — tests inject a stub. */
  graphApiBase?: string
}

/**
 * Fetch the Pages the user can publish to. Returns `SocialResult` — never
 * throws across the capability boundary.
 *
 * @param args        Capability args (cursor + limit). Ignored cursor on FB
 *                    because we walk pagination internally; surfaced limit
 *                    only as a soft cap on per-page size.
 * @param userToken   USER access token (passed via Authorization header).
 * @param ctx         Adapter call ctx — used for the redacted logger.
 * @param deps        Test seams.
 */
export async function listAccounts(
  args: SocialListAccountsArgs,
  userToken: string,
  ctx: SocialPostingCallCtx,
  deps: ListAccountsDeps = {},
): Promise<SocialResult<SocialListAccountsResult>> {
  const fetchImpl = deps.fetchImpl ?? fetch
  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE

  const configuredAccount = accountFromInstanceConfig(ctx.config)
  if (configuredAccount) {
    return {
      ok: true,
      data: { accounts: [configuredAccount] },
    }
  }

  if (typeof userToken !== 'string' || userToken.length === 0) {
    return {
      ok: false,
      error: {
        code: 'auth',
        message: 'Facebook user access token is missing or empty',
        retryable: false,
      },
    }
  }

  const limit = clampLimit(args?.limit)
  const initialPath =
    `${normalizeGraphBase(base)}/me/accounts` +
    `?fields=id,name,category,access_token,picture.type(large),followers_count&limit=${limit}`

  const seen = new Map<string, SocialAccount>()
  let nextUrl: string | null = initialPath
  let walked = 0

  while (nextUrl && walked < MAX_FETCH_PAGES) {
    walked += 1

    let response: Response
    try {
      response = await fetchImpl(nextUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
      })
    } catch (err) {
      ctx.redactedLogger.error('listAccounts network error', {
        page: walked,
        error: err instanceof Error ? err.message : 'unknown',
      })
      return {
        ok: false,
        error: {
          code: 'transient',
          message: 'Network error talking to Facebook Graph',
          retryable: true,
        },
      }
    }

    if (!response.ok) {
      const err = await errorFromResponse(response, 'Failed to list Facebook Pages')
      ctx.redactedLogger.warn('listAccounts provider error', {
        code: err.code,
        httpStatus: response.status,
        providerTraceId: err.providerTraceId,
      })
      return { ok: false, error: err }
    }

    const body = (await response.json().catch(() => null)) as FacebookPageAccountsListResponse | null
    if (!body || !Array.isArray(body.data)) {
      return {
        ok: false,
        error: {
          code: 'unknown',
          message: 'Facebook /me/accounts returned no data array',
          retryable: false,
        },
      }
    }

    for (const row of body.data) {
      const acct = rowToSocialAccount(row)
      if (acct && !seen.has(acct.providerAccountId)) {
        seen.set(acct.providerAccountId, acct)
      }
    }

    if (typeof body.paging?.next === 'string' && body.paging.next.length > 0) {
      if (isAllowedGraphUrl(body.paging.next, base)) {
        nextUrl = body.paging.next
      } else {
        ctx.redactedLogger.warn('listAccounts ignored off-origin paging cursor', {
          page: walked,
        })
        nextUrl = null
      }
    } else {
      nextUrl = null
    }
  }

  return {
    ok: true,
    data: {
      accounts: Array.from(seen.values()),
      // Marketing expects an opaque cursor; we never expose one because the
      // adapter exhausts pagination internally. Future: bound pagination
      // length and surface a cursor when we hit MAX_FETCH_PAGES.
    },
  }
}

// ============================================================================
// Helpers
// ============================================================================

function clampLimit(limit: number | undefined): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit) || limit <= 0) {
    return DEFAULT_LIMIT
  }
  // Meta clamps at 100 for /me/accounts.
  return Math.min(Math.floor(limit), 100)
}

function normalizeGraphBase(base: string): string {
  return base.replace(/\/+$/, '')
}

function isAllowedGraphUrl(candidate: string, base: string): boolean {
  try {
    const candidateUrl = new URL(candidate)
    const baseUrl = new URL(normalizeGraphBase(base))
    const basePath = baseUrl.pathname.replace(/\/+$/, '')

    return (
      candidateUrl.origin === baseUrl.origin &&
      (basePath.length === 0
        || candidateUrl.pathname === basePath
        || candidateUrl.pathname.startsWith(`${basePath}/`))
    )
  } catch {
    return false
  }
}

function rowToSocialAccount(row: FacebookPageAccountRow): SocialAccount | null {
  if (typeof row.id !== 'string' || row.id.length === 0) return null
  if (typeof row.name !== 'string') return null

  const avatarUrl = extractAvatarUrl(row.picture)

  return {
    id: row.id,
    providerAccountId: row.id,
    name: row.name,
    kind: 'facebook_page',
    avatarUrl,
    audienceSize: typeof row.followers_count === 'number' ? row.followers_count : undefined,
    metadata: {
      ...(row.category ? { category: row.category } : {}),
    },
  }
}

function accountFromInstanceConfig(config: unknown): SocialAccount | null {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null
  const pageId = readConfigString(config, 'pageId')
  if (!pageId) return null
  const pageName = readConfigString(config, 'pageName') ?? pageId

  return {
    id: pageId,
    providerAccountId: pageId,
    name: pageName,
    kind: 'facebook_page',
    metadata: { source: 'connector_config' },
  }
}

function readConfigString(config: object, key: string): string | null {
  const value = (config as Record<string, unknown>)[key]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function extractAvatarUrl(
  picture: FacebookPageAccountRow['picture'] | undefined,
): string | undefined {
  if (!picture) return undefined
  if (typeof picture === 'string') return picture
  if (typeof picture === 'object' && picture !== null) {
    const url = picture.data?.url
    return typeof url === 'string' && url.length > 0 ? url : undefined
  }
  return undefined
}
